var XPortal = XPortal || {};
XPortal.ChartRing = (function () {
    var drawing = kendo.drawing;
    var geometry = kendo.geometry;
    var Group = drawing.Group;

    var self = {
        Init: initRing,
        CombineSeries: combineSeriesIntoOne
    };

    function initRing(chartType, chartOpts, chartEl, stgs) {
        chartOpts.ring = {
            holeRatio: 0.84,
            gap: 0,
            minWidth: 1,
            overlap: 0.5,
            rounded: true,
            startAngle: 90,
            legendSpacing: 6,
            height: 300,
            minPlotWidth: 250,
            padding: 12,
            split: 0.6,
            showTotal: stgs.label.visible,
            template: stgs.label.template === "#= value #"
                ? "<small>Total</small><p>[[value]]</p>"
                : stgs.label.template
        };

        const ring = chartOpts.ring;
        let centre = null;
        let ringCentre = null;
        let stackExtra = null;
        let shapes = [];
        let collecting = false;

        chartOpts.transitions = false;
        chartOpts.seriesDefaults.type = "donut";
        const tooltip = kendo.template(stgs.hasCustomTooltip ? stgs.tooltip : "#= category #: #= value #");
        chartOpts.tooltip.template = e => tooltip($.extend({}, e, { series: $.extend({}, e.series, { name: e.category }) }));
        chartOpts.seriesDefaults.startAngle = ring.startAngle;
        chartOpts.seriesDefaults.missingValues = "gap";
        chartOpts.seriesDefaults.overlay = { gradient: "none" };
        chartOpts.seriesDefaults.color = null;
        chartOpts.seriesDefaults.padding = ring.padding;
        chartOpts.legend.align = "center";
        chartOpts.legend.padding = 0;
        chartOpts.arrange = (opts, width) => arrangeRing(opts, ring, width, stackExtra);
        chartOpts.arrange(chartOpts, chartEl.width());

        chartOpts.seriesDefaults.highlight = {
            toggle: function (e) {
                e.preventDefault();
                shapes.forEach(({ shape, dataItem, color, paint }) => {
                    if (dataItem !== e.dataItem) return;
                    shape.options.set(`${paint}.color`, e.show ? XPortal.Chart.shadeColor(color, -12) : color);
                });
            }
        };

        chartOpts.seriesDefaults.visual = e => {
            if (!collecting) { shapes = []; collecting = true; }

            const group = new Group();
            const color = e.dataItem?.color || e.options.color;
            const add = (shape, dataItem, shapeColor) => {
                group.append(shape);
                shapes.push({ shape, dataItem, color: shapeColor, paint: shape.options.stroke?.width ? "stroke" : "fill" });
            };

            const m = ringMetrics(e, ring);
            if (m) add(createRingSection(m, color), e.dataItem, color);

            if (XPortal.Chart.isLast(e)) {
                ringCentre = e.center;
                if (ring.showTotal) {
                    const points = XPortal.Chart.filterOutInvisible(e, e.series.data ?? []);
                    centre = {
                        x: e.center.x,
                        y: e.center.y,
                        hole: 2 * e.radius * ring.holeRatio,
                        total: roundTotal(points.reduce((sum, pt) => sum + (Number(pt.value) || 0), 0))
                    };
                }
            }
            return group;
        };

        const baseRender = chartOpts.render;
        chartOpts.render = e => {
            baseRender?.(e);
            renderRingTotal(chartEl, ring, stgs, centre);
            const opts = e.sender.options;
            const width = chartEl.width();
            const slack = opts.legend.position === "bottom" && ringCentre && width ? Math.round(ringCentre.y - ringCentre.x) : 0;
            centre = null;
            ringCentre = null;
            collecting = false;
            if (!slack) return;
            stackExtra = opts.chartArea.height - 2 * slack - width;
            if (opts.arrange(opts, width)) e.sender.redraw();
        };
    };

    function arrangeRing(opts, ring, width, stackExtra) {
        const points = opts.series[0]?.data ?? [];
        const font = { font: opts.legend.labels?.font };
        const rows = points.length;
        const row = kendo.util.measureText("Ag", font).height + ring.legendSpacing;
        const widest = Math.max(0, ...points.map(p => kendo.util.measureText(String(p.category ?? ""), font).width)) + row;
        const left = Math.round(Math.min(width * ring.split, width - widest));
        const compact = XPortal.Chart.isCompact(opts.legend.visible === false ? width : left, ring.minPlotWidth);
        const split = !compact && opts.legend.visible !== false;
        const height = compact ? Math.round(width + (stackExtra ?? 0)) : Math.max(ring.height, rows * row);
        const right = split ? Math.round(width - left) : 0;
        const legend = {
            position: compact ? "bottom" : "custom",
            orientation: compact ? "horizontal" : "vertical",
            margin: compact ? XPortal.Chart.XpandChartTheme.chart.legend.margin : 0,
            offsetX: compact ? 0 : left,
            offsetY: compact ? 0 : Math.round((height - rows * row) / 2)
        };
        const same = opts.chartArea.height === height && opts.plotArea?.margin?.right === right
            && Object.keys(legend).every(key => opts.legend[key] === legend[key]);
        if (same) return false;

        Object.assign(opts.legend, legend);
        opts.legend.labels = $.extend({}, opts.legend.labels, {
            margin: compact
                ? { top: ring.legendSpacing / 2, bottom: ring.legendSpacing / 2, left: ring.legendSpacing, right: ring.legendSpacing }
                : { top: ring.legendSpacing / 2, bottom: ring.legendSpacing / 2, left: 0, right: 0 }
        });
        opts.chartArea.height = height;
        opts.plotArea = $.extend(true, {}, opts.plotArea, { margin: { right } });
        return true;
    };

    function combineSeriesIntoOne(res) {
        const data = (res.series ?? []).map(s => {
            const points = s.data ?? [];
            const first = points[0] ?? {};
            const last = points.at(-1) ?? {};
            const resolved = [...new Set(points.map(p => p.color).filter(Boolean))];
            const link = s.link || points.find(p => p.link)?.link || first.link || "";
            return {
                category: s.name,
                value: roundTotal(points.reduce((sum, p) => sum + (Number(p.value) || 0), 0)),
                points: points.length,
                color: (resolved.length === 1 ? resolved[0] : "") || s.color || first.color,
                bgcolor: s.bgcolor || first.bgcolor,
                txtcolor: s.txtcolor || first.txtcolor,
                startdate: first.startdate,
                enddate: last.enddate,
                link: link
            };
        });

        const flat = data.length > 1 && data.every(d => d.color === data[0].color);
        if (flat) data.forEach(d => { d.color = ""; });

        res.series = [{ name: "", legendtemplate: "#=value#", legendvalue: "", data }];
        res.categories = data.map(d => d.category);
        XPortal.Chart.applyTopology(res.series);
        return res;
    };

    function roundTotal(value) {
        return Math.round(value * 1e6) / 1e6;
    };

    function ringLayout(e, floor) {
        const points = XPortal.Chart.filterOutInvisible(e, e.series?.data ?? []);
        const at = points.indexOf(e.dataItem);
        const total = points.reduce((sum, p) => sum + (Number(p.value) || 0), 0);
        if (at < 0 || !total) return null;

        const shares = points.map(p => 360 * (Number(p.value) || 0) / total);
        const room = Math.min(floor, 360 / points.length);
        const spare = shares.reduce((sum, w) => sum + Math.max(0, w - room), 0);
        const keep = spare > 0 ? (360 - points.length * room) / spare : 0;
        const drawn = shares.map(w => w <= room ? room : room + (w - room) * keep);
        const upto = (list, n) => list.slice(0, n).reduce((sum, w) => sum + w, 0);
        return {
            start: e.startAngle - upto(shares, at) + upto(drawn, at),
            sweep: drawn[at],
            before: drawn[(at || points.length) - 1]
        };
    };

    function ringCorner(sweep, outer, inner, rounded) {
        const thickness = outer - inner;
        const none = { radius: 0, outerOffset: 0, innerOffset: 0, span: 0 };
        if (!rounded || !(sweep > 0) || !(thickness > 0)) return none;
        const radius = thickness / 2;
        if (!(radius > 0.5)) return none;
        const deg = ratio => Math.asin(Math.min(1, ratio)) * 180 / Math.PI;
        const outerOffset = deg(radius / (outer - radius));
        const innerOffset = deg(radius / (inner + radius));
        return { radius, outerOffset, innerOffset, span: Math.max(outerOffset, innerOffset) };
    };

    function ringMetrics(e, ring) {
        const outer = e.radius;
        const inner = outer * ring.holeRatio;
        const thickness = outer - inner;
        if (!(outer > 0) || !(thickness > 0) || !(e.endAngle > e.startAngle)) return null;

        const mid = (outer + inner) / 2;
        const toDeg = px => (px / mid) * 180 / Math.PI;
        const layout = ringLayout(e, toDeg(ring.minWidth));
        if (!layout) return null;

        const sweep = layout.sweep;
        const corner = ringCorner(360, outer, inner, ring.rounded);
        const pad = Math.min(toDeg(ring.gap) / 2, sweep / 2);
        const before = ringCorner(layout.before, outer, inner, ring.rounded);
        const capEnd = layout.start + sweep - pad;
        return {
            center: new geometry.Point(e.center.x, e.center.y),
            outer,
            inner,
            mid,
            thickness,
            sweep,
            corner,
            head: capEnd - corner.span,
            tail: layout.start + pad - before.span - toDeg(ring.overlap)
        };
    };

    function ringPointAt(center, radius, deg) {
        const a = deg * Math.PI / 180;
        return { x: center.x + radius * Math.cos(a), y: center.y + radius * Math.sin(a) };
    };

    function createRingSection(m, color) {
        if (m.sweep >= 359.99) {
            return new drawing.Circle(new geometry.Circle(m.center, m.mid),
                { stroke: { color, width: m.thickness, lineCap: "butt" }, fill: null });
        }

        const { outer, inner, head, tail } = m;
        const r = m.corner.radius;
        const start = ringPointAt(m.center, outer, tail);
        return new drawing.Path({ fill: { color }, stroke: { color: "none" } })
            .moveTo(start.x, start.y)
            .arc(tail, head, outer, outer, false)
            .arc(head, head + 90, r, r, false)
            .arc(head + 90, head + 180, r, r, false)
            .arc(head, tail, inner, inner, true)
            .arc(tail + 180, tail + 90, r, r, true)
            .arc(tail + 90, tail, r, r, true)
            .close();
    };

    function renderRingTotal(chartEl, ring, stgs, centre) {
        let node = chartEl.children(".ring-total");
        if (!centre) { node.remove(); return; }
        if (!node.length) node = $('<div class="ring-total"></div>').appendTo(chartEl);
        node.html(ring.template.replace(/\[\[\s*(?:value|total|amount)\s*\]\]|#[=:]\s*value\s*#/gi,
            kendo.toString(Math.round(centre.total), "n0")));
        node.css({ left: `${centre.x}px`, top: `${centre.y}px`, maxWidth: `${centre.hole}px`, color: stgs.txColor || "" });
    };
    return self;
}());
